<template>
  <div class="common-layout">
    <el-container>
      <el-aside width="250px" class="border-r-2">
        <div class="mt-5 px-4 py-6 rounded-lg">
					<i class="pi pi-file mb-4" style="font-size: 3rem"></i>
          <h2 class="title-font font-medium text-2xl text-gray-900">
            關鍵能力問卷
          </h2>
          <p class="leading-relaxed">Questionnaire</p>
        </div>
        <ul>
          <li class="w-full">
            <a
              @click="goToRecordsAndExport"
              class="adminLink text-lg inline-block p-4 w-full bg-gray-100 hover:text-gray-70 rounded-r-lg hover:bg-gray-200 focus:ring-4 focus:ring-blue-300 focus:outline-none"
            >
              紀錄 & 匯出
            </a>
          </li>
          <li class="w-full">
            <a
              @click="goToSetting"
              class="adminLink text-lg inline-block p-4 w-full hover:text-gray-70 rounded-r-lg hover:bg-gray-200 focus:ring-4 focus:ring-blue-300 focus:outline-none"
            >
              問卷設定
            </a>
          </li>
          <li class="w-full">
            <a
              @click="goBack"
              class="adminLink text-lg inline-block p-4 w-full hover:text-gray-70 rounded-r-lg hover:bg-gray-200 focus:ring-4 focus:ring-blue-300 focus:outline-none"
            >
              返回上一頁
            </a>
          </li>
        </ul>
      </el-aside>
      <el-main>
        <!-- 問卷匯出區域 -->
        <div
          class="border rounded-lg text-white p-5 m-5 text-left"
          style="background-color: #2db3b3"
        >
          <div class="widget widget-info flex" style="padding-bottom: 0">
            <h3 class="widget-title">問卷資料匯出</h3>
          </div>
          <div class="grid gap-6 p-5 md:grid-cols-2">
            <div>
              <label class="block mb-2 text-md font-medium text-white-900">
                <span style="color: red"> * </span>學期
              </label>
              <select
                v-model="form.semester"
                class="bg-gray-50 border border-gray-300 text-gray-900 text-md rounded-lg block w-full h-14 px-3"
              >
                <option value="">請選擇學期</option>
                <option v-for="item in semesters" :key="item" :value="item">
                  {{ item }}
                </option>
              </select>
            </div>
            <div>
              <label class="block mb-2 text-md font-medium text-white-900">
                學號前三碼（可多筆）
              </label>
              <div class="bg-gray-50 border border-gray-300 text-gray-900 text-md rounded-lg block w-full h-14 px-3">
                <div class="flex h-full items-center gap-2 overflow-x-auto whitespace-nowrap">
                  <span
                    v-for="(prefix, index) in form.gradePrefixes"
                    :key="prefix + '_' + index"
                    class="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-800 rounded shrink-0"
                  >
                    {{ prefix }}
                    <button
                      type="button"
                      class="text-teal-700 hover:text-teal-900"
                      @click="removeGradePrefix(index)"
                    >
                      ×
                    </button>
                  </span>
                  <input
                    v-model="form.gradePrefixInput"
                    class="flex-1 min-w-[140px] bg-transparent outline-none"
                    placeholder="輸入後按 Enter 新增（例如 110）"
                    maxlength="3"
                    inputmode="numeric"
                    @input="normalizeGradePrefixInput"
                    @keydown.enter.prevent="addGradePrefix"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="grid gap-6 px-5 pb-5 md:grid-cols-4">
            <div>
              <label class="block mb-2 text-md font-medium text-white-900">
                開始日期
              </label>
              <div class="date-input-wrap">
                <Calendar
                  v-model="form.dateStart"
                  :showIcon="true"
                  dateFormat="yy-mm-dd"
                  placeholder="開始日期"
                  class="w-full export-date-input"
                />
                <button
                  v-if="form.dateStart"
                  type="button"
                  class="date-clear-btn"
                  @click.stop="form.dateStart = null"
                >
                  ×
                </button>
              </div>
            </div>
            <div>
              <label class="block mb-2 text-md font-medium text-white-900">
                結束日期
              </label>
              <div class="date-input-wrap">
                <Calendar
                  v-model="form.dateEnd"
                  :showIcon="true"
                  dateFormat="yy-mm-dd"
                  placeholder="結束日期"
                  class="w-full export-date-input"
                />
                <button
                  v-if="form.dateEnd"
                  type="button"
                  class="date-clear-btn"
                  @click.stop="form.dateEnd = null"
                >
                  ×
                </button>
              </div>
            </div>
            <div class="md:col-span-2">
              <label class="block mb-2 text-md font-medium text-white-900">
                抽獎筆數
              </label>
              <InputText
                v-model="form.drawCount"
                class="w-full export-draw-input"
                placeholder="填數字，例如 5"
                inputmode="numeric"
                @input="normalizeDrawCount"
              />
            </div>
          </div>
          <div class="p-5 pt-0 text-center">
            <button
              type="button"
              class="text-white px-10 py-3 rounded-lg"
              style="background-color: #1e8f8f"
              :disabled="isExporting"
              @click="exportExcel"
            >
              {{ isExporting ? "匯出中..." : "匯出 Excel" }}
            </button>
          </div>
        </div>
		<!-- 填寫紀錄區域 -->
        <div class="bg-white rounded-lg shadow-md p-6 m-5 mb-8">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-xl font-bold text-gray-800">學生填寫紀錄</h3>
          </div>

          <div class="grid grid-cols-1 gap-4 mb-4 lg:grid-cols-5">
            <div class="flex items-center gap-2 w-full">
              <InputText
                v-model="filterSemesterYear"
                class="flex-1 text-center"
                placeholder="114"
                maxlength="3"
                inputmode="numeric"
                @input="normalizeFilterSemesterYear()"
              />
              <span class="text-gray-500">-</span>
              <InputText
                v-model="filterSemesterTerm"
                class="flex-1 text-center"
                placeholder="2"
                maxlength="1"
                inputmode="numeric"
                @input="normalizeFilterSemesterTerm()"
              />
            </div>
            <InputText
              v-model="filterForm.stuId"
              placeholder="學號"
              class="w-full"
            />
            <InputText
              v-model="filterForm.stuName"
              placeholder="姓名"
              class="w-full"
            />
            <div class="date-input-wrap">
              <Calendar
                v-model="filterForm.dateStart"
                :showIcon="true"
                dateFormat="yy-mm-dd"
                placeholder="開始日期"
                class="w-full filter-date-input"
              />
              <button
                v-if="filterForm.dateStart"
                type="button"
                class="date-clear-btn"
                @click.stop="filterForm.dateStart = null"
              >
                ×
              </button>
            </div>
            <div class="date-input-wrap">
              <Calendar
                v-model="filterForm.dateEnd"
                :showIcon="true"
                dateFormat="yy-mm-dd"
                placeholder="結束日期"
                class="w-full filter-date-input"
              />
              <button
                v-if="filterForm.dateEnd"
                type="button"
                class="date-clear-btn"
                @click.stop="filterForm.dateEnd = null"
              >
                ×
              </button>
            </div>
          </div>
          <div class="flex items-center gap-2 mb-6">
            <button
              type="button"
              class="text-white p-2 px-5 rounded-lg"
              style="background-color: #2db3b3"
              @click="applyFilters"
              :disabled="isLoadingRecords"
            >
              查詢
            </button>
            <button
              type="button"
              class="text-gray-700 bg-gray-100 p-2 px-5 rounded-lg"
              @click="resetFilters"
              :disabled="isLoadingRecords"
            >
              清除
            </button>
            <span class="text-sm text-gray-500">
              每頁 15 筆
            </span>
          </div>

          <DataTable
            :value="records"
            :paginator="true"
            :rows="rowsPerPage"
            :first="firstRow"
            :totalRecords="totalRecords"
            :lazy="true"
            :loading="isLoadingRecords"
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
            currentPageReportTemplate="顯示 {first} 到 {last} 筆，共 {totalRecords} 筆"
            responsiveLayout="scroll"
            class="p-datatable-sm"
            @page="onPageChange"
          >
            <template #empty>
              <div class="text-center text-lg">無資料</div>
            </template>
            <Column field="seq" header="#" style="width: 6%"></Column>
            <Column field="semester" header="學期" style="width: 10%"></Column>
            <Column field="stuID" header="學號" style="width: 12%"></Column>
            <Column field="stuName" header="姓名" style="width: 12%"></Column>
            <Column field="stuDept" header="系所" style="width: 20%"></Column>
            <Column field="apply_time" header="填寫時間" style="width: 20%">
              <template #body="{ data }">
                {{ formatApplyTime(data.apply_time) }}
              </template>
            </Column>
            <Column field="avg_score" header="平均分數" style="width: 10%">
              <template #body="{ data }">
                {{ formatAverageScore(data.avg_score) }}
              </template>
            </Column>
          </DataTable>
        </div>
      </el-main>
    </el-container>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";
import InputText from "primevue/inputtext";
import Calendar from "primevue/calendar";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import { ElNotification } from "element-plus";

export default {
  components: {
    InputText,
    Calendar,
    DataTable,
    Column,
  },
  setup() {
    const router = useRouter();
    const semesters = ref([]);
    const isExporting = ref(false);
    const form = ref({
      semester: "",
      gradePrefixes: [],
      gradePrefixInput: "",
      dateStart: null,
      dateEnd: null,
      drawCount: "",
    });

    // 填寫紀錄相關
    const records = ref([]);
    const isLoadingRecords = ref(false);
    const rowsPerPage = ref(15);
    const firstRow = ref(0);
    const currentPage = ref(1);
    const totalRecords = ref(0);
    const filterForm = ref({
      semester: "",
      stuId: "",
      stuName: "",
      dateStart: null,
      dateEnd: null,
    });
    const filterSemesterYear = ref("");
    const filterSemesterTerm = ref("");

    const goBack = () => {
      router.push({ path: "/sladmin" });
    };

    const goToSetting = () => {
      router.push({ path: "/sladmin/questionnaire-setting" });
    };

    const goToRecordsAndExport = () => {
      router.push({ path: "/sladmin/questionnaire" });
    };

    const normalizeGradePrefixInput = () => {
      form.value.gradePrefixInput = form.value.gradePrefixInput
        .replace(/\D/g, "")
        .slice(0, 3);
    };

    const addGradePrefix = () => {
      const value = String(form.value.gradePrefixInput || "").trim();
      if (!value) {
        return;
      }

      if (value.length !== 3) {
        ElNotification({
          title: "提醒",
          message: "請輸入學號前三碼（3 碼）",
          type: "warning",
        });
        return;
      }

      if (!form.value.gradePrefixes.includes(value)) {
        form.value.gradePrefixes.push(value);
      }
      form.value.gradePrefixInput = "";
    };

    const removeGradePrefix = (index) => {
      form.value.gradePrefixes.splice(index, 1);
    };

    const normalizeDrawCount = () => {
      form.value.drawCount = String(form.value.drawCount || "")
        .replace(/\D/g, "")
        .slice(0, 6);
    };

    const fetchSemesters = async () => {
      try {
        const response = await axios.get(
          process.env.VUE_APP_api_url + "/questionnaire/ability/semesters"
        );
        if (response.data && response.data.status === true) {
          semesters.value = response.data.data || [];
        }
      } catch (error) {
        console.error("取得學期失敗:", error);
        ElNotification({
          title: "錯誤",
          message: "無法載入學期清單",
          type: "error",
        });
      }
    };

    const exportExcel = async () => {
      if (isExporting.value) {
        return;
      }

      if (!form.value.semester) {
        ElNotification({
          title: "提醒",
          message: "請選擇學期",
          type: "warning",
        });
        return;
      }

      if (form.value.gradePrefixInput) {
        addGradePrefix();
      }

      if (form.value.gradePrefixes.some((item) => item.length !== 3)) {
        ElNotification({
          title: "提醒",
          message: "請確認每個條件都是學號前三碼（3 碼）",
          type: "warning",
        });
        return;
      }

      if (form.value.drawCount && Number(form.value.drawCount) <= 0) {
        ElNotification({
          title: "提醒",
          message: "抽獎筆數請填大於 0 的整數",
          type: "warning",
        });
        return;
      }

      isExporting.value = true;
      try {
        const exportDateRange = {
          start: form.value.dateStart ? formatDateTime(form.value.dateStart) : "",
          end: form.value.dateEnd ? formatDateTime(form.value.dateEnd) : "",
        };

        const response = await axios.post(
          process.env.VUE_APP_api_url + "/questionnaire/ability/export",
          {
            semester: form.value.semester,
            gradePrefixes: form.value.gradePrefixes.length
              ? form.value.gradePrefixes
              : undefined,
            date_start: exportDateRange.start || undefined,
            date_end: exportDateRange.end || undefined,
            draw_count: form.value.drawCount
              ? Number(form.value.drawCount)
              : undefined,
          }
        );

        const downloadAnchor = document.createElement("a");
        downloadAnchor.setAttribute(
          "href",
          process.env.VUE_APP_api_url + response.data
        );
        document.body.appendChild(downloadAnchor);
        downloadAnchor.click();
        document.body.removeChild(downloadAnchor);
      } catch (error) {
        console.error("匯出失敗:", error);
        ElNotification({
          title: "錯誤",
          message: error?.response?.data?.message || "匯出失敗，請稍後再試",
          type: "error",
        });
      } finally {
        isExporting.value = false;
      }
    };

    // 填寫紀錄相關函數
    const parseServerDate = (value) => {
      if (!value) {
        return null;
      }

      if (value instanceof Date) {
        return value;
      }

      if (typeof value === "string") {
        if (value.includes("T")) {
          return new Date(value);
        }
        return new Date(value.replace(" ", "T") + "+08:00");
      }

      return new Date(value);
    };

    const formatDateTime = (value) => {
      if (!value) {
        return "";
      }

      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) {
        return "";
      }

      const pad = (number) => String(number).padStart(2, "0");
      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());
      const hour = pad(date.getHours());
      const minute = pad(date.getMinutes());
      const second = pad(date.getSeconds());

      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    };

    const formatApplyTime = (value) => {
      if (!value) {
        return "-";
      }

      const dateValue = parseServerDate(value);
      if (!dateValue || Number.isNaN(dateValue.getTime())) {
        return value;
      }

      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Taipei",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      const parts = formatter.formatToParts(dateValue);
      const partMap = Object.fromEntries(
        parts.map((part) => [part.type, part.value])
      );

      return `${partMap.year}/${partMap.month}/${partMap.day} ${partMap.hour}:${partMap.minute}`;
    };

    const formatAverageScore = (value) => {
      if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "-";
      }

      return Number(value).toFixed(2);
    };

    const buildDateRange = () => {
      const start = filterForm.value.dateStart
        ? formatDateTime(filterForm.value.dateStart)
        : "";
      const end = filterForm.value.dateEnd
        ? formatDateTime(filterForm.value.dateEnd)
        : "";

      return { start, end };
    };

    const buildSemesterFilter = () => {
      const year = filterSemesterYear.value.trim();
      const term = filterSemesterTerm.value.trim();
      if (!year && !term) {
        return "";
      }
      if (/^\d{3}$/.test(year) && /^[12]$/.test(term)) {
        return `${year}-${term}`;
      }
      return "__invalid__";
    };

    const fetchRecords = async (page = currentPage.value) => {
      isLoadingRecords.value = true;
      try {
        const range = buildDateRange();
        const semesterFilter = buildSemesterFilter();
        if (semesterFilter === "__invalid__") {
          ElNotification({
            title: "提醒",
            message: "學期查詢請填三位數年份與 1 或 2",
            type: "warning",
          });
          records.value = [];
          totalRecords.value = 0;
          return;
        }
        const params = {
          semester: semesterFilter || undefined,
          stu_id: filterForm.value.stuId || undefined,
          stu_name: filterForm.value.stuName || undefined,
          date_start: range.start || undefined,
          date_end: range.end || undefined,
          page,
          limit: rowsPerPage.value,
        };

        const response = await axios.get(
          process.env.VUE_APP_api_url + "/questionnaire/ability/admin/records",
          { params }
        );

        if (response.data && response.data.status === true) {
          records.value = response.data.data || [];
          totalRecords.value = Number(
            response.data?.pagination?.total ?? records.value.length
          );
          currentPage.value = Number(response.data?.pagination?.page || page);
          firstRow.value = (currentPage.value - 1) * rowsPerPage.value;
        } else {
          records.value = [];
          totalRecords.value = 0;
        }
      } catch (error) {
        console.error("取得問卷紀錄失敗:", error);
        ElNotification({
          title: "錯誤",
          message: "無法載入問卷紀錄",
          type: "error",
        });
        records.value = [];
        totalRecords.value = 0;
      } finally {
        isLoadingRecords.value = false;
      }
    };

    const applyFilters = () => {
      currentPage.value = 1;
      firstRow.value = 0;
      fetchRecords(1);
    };

    const resetFilters = () => {
      filterForm.value = {
        semester: "",
        stuId: "",
        stuName: "",
        dateStart: null,
        dateEnd: null,
      };
      filterSemesterYear.value = "";
      filterSemesterTerm.value = "";
      currentPage.value = 1;
      firstRow.value = 0;
      fetchRecords(1);
    };

    const onPageChange = (event) => {
      rowsPerPage.value = event.rows || rowsPerPage.value;
      firstRow.value = event.first || 0;
      const targetPage = Math.floor(firstRow.value / rowsPerPage.value) + 1;
      currentPage.value = targetPage;
      fetchRecords(targetPage);
    };

    const normalizeFilterSemesterYear = () => {
      filterSemesterYear.value = filterSemesterYear.value
        .replace(/\D/g, "")
        .slice(0, 3);
    };

    const normalizeFilterSemesterTerm = () => {
      filterSemesterTerm.value = filterSemesterTerm.value
        .replace(/\D/g, "")
        .slice(0, 1);
      if (
        filterSemesterTerm.value &&
        !["1", "2"].includes(filterSemesterTerm.value)
      ) {
        filterSemesterTerm.value = "";
      }
    };

    onMounted(() => {
      fetchSemesters();
      fetchRecords(1);
    });

    return {
      goBack,
      goToSetting,
      goToRecordsAndExport,
      semesters,
      form,
      isExporting,
      normalizeGradePrefixInput,
      addGradePrefix,
      removeGradePrefix,
      normalizeDrawCount,
      exportExcel,
      // 填寫紀錄
      records,
      isLoadingRecords,
      rowsPerPage,
      firstRow,
      currentPage,
      totalRecords,
      filterForm,
      filterSemesterYear,
      filterSemesterTerm,
      formatApplyTime,
      formatAverageScore,
      applyFilters,
      resetFilters,
      onPageChange,
      normalizeFilterSemesterYear,
      normalizeFilterSemesterTerm,
    };
  },
};
</script>

<style scoped>
:deep(.date-input-wrap) {
  position: relative;
}

:deep(.date-clear-btn) {
  position: absolute;
  right: 3.05rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.3rem;
  height: 1.3rem;
  border-radius: 9999px;
  border: none;
  background: #e5e7eb;
  color: #374151;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

:deep(.date-clear-btn:hover) {
  background: #d1d5db;
}

:deep(.export-date-input .p-inputtext) {
  border-radius: 0.5rem;
  padding-right: 5.2rem;
}

:deep(.export-date-input .p-datepicker-trigger) {
  border-top-right-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
}

:deep(.filter-date-input .p-inputtext) {
  padding-right: 5.2rem;
}

:deep(.export-draw-input),
:deep(.export-draw-input.p-inputtext) {
  border-radius: 0.5rem !important;
}
</style>
